import type { OrderHistoryTab } from "@/features/orders-and-requests/types";
import type {
  OrderHistoryItemStatus,
  OrderHistoryItemType,
} from "@/graphql/orderHistory";

export const ORDER_TYPE = {
  FOOD_ORDER: "FOOD_ORDER",
  SERVICE_REQUEST: "SERVICE_REQUEST",
} as const satisfies Record<OrderHistoryItemType, OrderHistoryItemType>;

export const CONTACT_FRONTDESK_PAGE = {
  FOOD_ORDER: "foodOrder",
  SERVICE_REQUEST: "serviceRequest",
  IRD_ACCESS_CODE: "irdAccessCode",
} as const;

export type ContactFrontdeskPage =
  (typeof CONTACT_FRONTDESK_PAGE)[keyof typeof CONTACT_FRONTDESK_PAGE];

export const SELLER_CATEGORY = {
  RESTAURANT: "Restaurant",
  BAR: "Bar",
  CAFE: "Cafe",
  LAUNDRY: "Laundry",
} as const;

export const SERVICE_REQUEST_CART_KEY = "service-request";

export const DEFAULT_CATEGORY_ID = "default";

export type SellerCategory =
  (typeof SELLER_CATEGORY)[keyof typeof SELLER_CATEGORY];

export const ORDER_HISTORY_ITEM_STATUS = {
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
} as const satisfies Record<OrderHistoryItemStatus, OrderHistoryItemStatus>;

export const STATUS_BADGE_CONFIG: Record<
  OrderHistoryItemStatus,
  { bg: string; label: string }
> = {
  IN_PROGRESS: { bg: "bg-warning-100", label: "In Progress" },
  COMPLETED: { bg: "bg-teal-100", label: "Completed" },
  CANCELLED: { bg: "bg-error-100", label: "Cancelled" },
};

export const SERVICE_REQUEST_TAB_ID: OrderHistoryTab = "service";

export const ORDER_HISTORY_TABS: Array<{ id: OrderHistoryTab; label: string }> =
  [
    { id: "all", label: "All Orders" },
    { id: "food", label: "Food Orders" },
    { id: SERVICE_REQUEST_TAB_ID, label: "Service Requests" },
  ];

export const normalizeCategoryName = (
  categoryName: string | null | undefined,
) => categoryName?.trim().toLowerCase() ?? "";

export const isSellerCategory = (
  categoryName: string | null | undefined,
  category: SellerCategory,
) => normalizeCategoryName(categoryName) === category.toLowerCase();

export const isRestaurantSeller = (categoryName: string | null | undefined) =>
  isSellerCategory(categoryName, SELLER_CATEGORY.RESTAURANT);

export const FOOD_SELLER_TYPES = new Set<string>(
  [SELLER_CATEGORY.RESTAURANT, SELLER_CATEGORY.BAR, SELLER_CATEGORY.CAFE].map(
    normalizeCategoryName,
  ),
);

export const isFoodSellerCategory = (categoryName: string | null | undefined) =>
  FOOD_SELLER_TYPES.has(normalizeCategoryName(categoryName));

export const EMPTY_STATE_LABELS: Record<OrderHistoryTab, string> = {
  all: "No orders found",
  food: "No food orders found",
  service: "No service requests found",
};

export const ACTIVE_ORDER_TOAST_CLASS =
  "w-[calc(100vw-2rem)] max-w-md bg-primary-75 rounded-xs8 px-3 py-2 shadow-none [&>div:first-child]:!items-start [&>div:first-child]:!gap-0";

export const ORDER_SERVE_TIME = "30-45 mins";

export const ORDER_NOTE = `Order, once confirmed, is considered final. It usually takes ${ORDER_SERVE_TIME} to serve you. Thank you for your understanding and support.`;

export const BOOKING_FOOTER_HEIGHT_PX = 77;
export const TOAST_BOTTOM_GAP_PX = 16;
export const TOAST_Z_INDEX = 45;

export const LOGGED_IN_PHONE_COUNTRY_CODE = "+91";

export const ROOM_ACCESS_CODE_ERROR_MESSAGES = {
  VERIFICATION_FAILED: "Invalid code, please enter the correct code.",
  ATTEMPTS_EXCEEDED:
    "Access Code attempts exceeded. Please contact the front desk for assistance.",
} as const;

export const PRICE_DECIMAL_PLACES = 2;
